<template>
  <div class="sector discount-code-sector">
    <div class="row">
      <div class="col-12">
        <el-button class="btn-use-discount-code" type="primary" size="large" @click="onClickDiscountCode">
          <el-icon class="el-icon--right">
            <Ticket />
          </el-icon>
          {{ $t("carts.useDiscountCode") }}
        </el-button>
        <div v-if="form.prom_code_id">
          <PromotionCodeItem :form="form" :item="state.discountCode" :code-state="codeState" isSelected isValid
            @click="removeDiscountCode" @allowUse="(val) => {
              allowUseDiscountCode(val);
            }" />
        </div>
      </div>
    </div>
    <el-dialog v-model="codeState.visible" :width="isMobile ? '100%' : '600px'" :fullscreen="isMobile"
      modal-class="el-dialog-use-discount-code">
      <template #header>
        <span class="text-h5">
          {{ $t("carts.useDiscountCode") }}
        </span>
      </template>
      <el-card body-style="padding: 8px;">
        <el-card class="mb-4" body-style="padding: 8px;">
          <template #header>
            <span class="font-weight-bold subtitle-1">
              請輸入折扣碼
            </span>
          </template>
          <el-form ref="codeFormRef" :model="codeState">
            <el-form-item prop="code" :rules="[{ validator: validateCode, trigger: 'blur' }]">
              <el-input v-model="codeState.code" size="large" maxlength="20" clearable :placeholder="$t('輸入折扣碼')"
                @change="chkCode">
              </el-input>
            </el-form-item>
            <div class="flex justify-content-end">
              <el-button type="primary" size="large" :disabled="!codeState.code" @click="handleDiscountCode(codeFormRef)">
                {{ $t("global.use") }}
              </el-button>
            </div>
          </el-form>
        </el-card>
        <el-card body-style="padding: 8px;">
          <template #header>
            <span class="font-weight-bold subtitle-1">
              請選擇一個折扣碼
            </span>
          </template>
          <el-scrollbar>
            <el-empty v-if="!codeState.items?.length" :description="$t('carts.emptyDiscountCode')" />
            <PromotionCodeItem v-for="item in codeState.items" :key="`user-discount-code-${item.id}`" :form="form"
              :item="item" :code-state="codeState" @click="onClickPromo" />
          </el-scrollbar>
        </el-card>
      </el-card>
    </el-dialog>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from "vue";
import { useVModel } from "@vueuse/core";
import { useStore } from 'vuex'
import { useI18n } from "vue-i18n"
import dayjs from "dayjs";
import { Ticket } from '@element-plus/icons-vue';
import {
  breakpoint,
  notificationErrors,
} from "../../../utils";
import {
  _api_getAccountPromotionCodes,
  _api_postAccountPromotionCodes,
} from "../../../api/index.js";
import PromotionCodeItem from "./PromotionCodeItem.vue";

const props = defineProps({
  modelValue: Object,
  state: Object,
  promotionMap: Object,
});
const emit = defineEmits(["update:modelValue", "ok"]);

const form = useVModel(props, "modelValue", emit);

const { t, n, locale } = useI18n()
const store = useStore()

const isMobile = computed(() => breakpoint.value.xs || breakpoint.value.sm)
// const storeId = computed(() => store.getters["lite/storeData"]?.id);

const promotionCodeDiscount = computed(() => {
  const discountCode = codeState.data
    ? codeState.data
    : props.state.discountCode;
  if (form.value.prom_code_id && discountCode) {
    if (discountCode?.promotion?.discount_type === 'amount') {
      return discountCode?.promotion?.conditions[0].discount_amount;
    } else if (discountCode?.promotion?.discount_type === 'percentage') {
      let amount = (discountCode?.promotion?.conditions[0].discount_percentage * codeState.amount) / 100;
      if (discountCode?.promotion?.conditions[0].discount_amount && amount > discountCode?.promotion?.conditions[0].discount_amount) {
        amount = discountCode?.promotion?.conditions[0].discount_amount;
      }
      return Math.round(amount);
    }
  }
  return 0;
});

const codeState = reactive({
  visible: false,
  amount: 0,
  code: null,
  data: null,
  items: [],
  params: {
    status: 'now',
    skip_page: true,
    // store_id: storeId.value,
    fields: 'promotion',
  },
  currency: props.state.currency_symbol,
});

const codeFormRef = ref();

const onClickDiscountCode = async () => {
  store.dispatch("lite/setFullPageLoading", true);
  try {
    codeState.params.starting_after = null;
    const resp = await _api_getAccountPromotionCodes({
      ...codeState.params,
      // store_id: storeId.value,
    });
    codeState.items = resp.data.data;
  } catch (err) {
    console.error(err);
    notificationErrors(err);
  } finally {
    store.dispatch("lite/setFullPageLoading", false);
  }
  codeState.visible = true;
};

const chkCode = (value) => {
  const item = codeState.items.find((el) => el.promotion.codes[0] === value);
  if (item) {
    codeState.data = item;
  } else {
    codeState.data = null;
  }
};

const isValidDate = () => {
  if (!codeState.data?.promotion?.start_at) {
    return false;
  }
  const unit = 'minute';
  const startChk = dayjs().isSameOrAfter(codeState.data?.promotion?.start_at, unit);
  if (codeState.data?.promotion?.ended_at) {
    const endChk = dayjs().isBetween(
      dayjs(codeState.data?.promotion?.start_at),
      dayjs(codeState.data?.promotion?.ended_at),
      unit,
    );
    return endChk;
  }
  return startChk;
};

// 比對重複的值
const chkProductIds = (arr1, arr2) => arr1.filter((val) => arr2.includes(val));

const validateCode = (rule, value) => {
  if (!codeState.data || !value) {
    return Promise.reject("請先選擇折扣碼");
  } else {
    // console.log("data: ", value, codeState.data)

    // 檢查使用期限
    if (!isValidDate()) {
      return Promise.reject("此折扣碼已過期或尚未開始");
    }

    // 檢查使用次數
    if (
      codeState.data?.promotion?.max_use_count &&
      codeState.data.promotion.max_use_count <=
      codeState.data.promotion.use_count
    ) {
      return Promise.reject("已無剩餘使用次數");
    }
    const conditionScope = codeState.data?.promotion?.condition_scope;
    // console.debug("conditionScope: ", conditionScope);
    const carts = form.value.carts.filter(
      (el) => el.checked && !el?.is_excluded_discount
    );
    // if (["categories", "products", "subtotal"].includes(conditionScope)) {
    //   carts = carts.filter((el) => el.checked && !el?.is_excluded_discount);
    // } else if (["promotions", "promotion_groups"].includes(conditionScope)) {
    //   carts = carts.filter((el) => el.checked );
    // }
    // console.debug("carts: ", carts);
    if (!carts?.length) {
      return Promise.reject("購物車中的商品不適用折扣碼");
    }
    codeState.amount = 0;
    if (conditionScope == "categories") {
      // 指定分類
      let num = 0;
      carts.forEach((cart) => {
        const chkCat = chkProductIds(
          codeState.data.promotion?.conditions[0].whitelisted_store_cat_ids,
          cart.store_cat_ids
        );
        // console.log("chkCat: ", chkCat)

        // 檢查排除assort
        if (chkCat?.length) {
          if (
            codeState.data?.promotion?.conditions[0]
              .blacklisted_store_assort_ids &&
            !!codeState.data.promotion?.conditions[0]
              .blacklisted_store_assort_ids?.length
          ) {
            if (
              !codeState.data.promotion?.conditions[0].blacklisted_store_assort_ids.includes(
                cart.store_assort_id
              )
            ) {
              num++;
              codeState.amount += cart.sub_total;
            }
          } else {
            num++;
            codeState.amount += cart.sub_total;
          }
        }
      });
      // console.log("num: ", num)
      if (!num) {
        return Promise.reject("購物車中的商品不適用此折扣碼");
      }
    } else if (conditionScope == "products") {
      // 指定商品
      let num = 0;
      carts.forEach((cart) => {
        if (
          codeState.data.promotion?.conditions[0].whitelisted_store_product_ids.includes(
            cart.store_product_id
          )
        ) {
          // 檢查排除assort
          if (
            codeState.data?.promotion?.conditions[0]
              .blacklisted_store_assort_ids &&
            !!codeState.data.promotion?.conditions[0]
              .blacklisted_store_assort_ids?.length
          ) {
            if (
              !codeState.data?.promotion?.conditions[0].blacklisted_store_assort_ids.includes(
                cart.store_assort_id
              )
            ) {
              codeState.amount += cart.sub_total;
              num++;
            }
          } else {
            codeState.amount += cart.sub_total;
            num++;
          }
        }
      });
      if (!num) {
        return Promise.reject("購物車中的商品不適用此折扣碼");
      }
    } else if (conditionScope == "subtotal") {
      // 全站商品
      let num = 0;
      carts.forEach((cart) => {
        // 檢查排除assort
        if (
          codeState.data?.promotion?.conditions[0]
            .blacklisted_store_assort_ids &&
          !!codeState.data.promotion?.conditions[0].blacklisted_store_assort_ids
            ?.length
        ) {
          if (
            !codeState.data?.promotion?.conditions[0].blacklisted_store_assort_ids.includes(
              cart.store_assort_id
            )
          ) {
            codeState.amount += cart.sub_total;
            num++;
          }
        } else {
          codeState.amount += cart.sub_total;
          num++;
        }
      });
      if (!num) {
        return Promise.reject("購物車中的商品不適用此折扣碼");
      }
    } else if (conditionScope == "promotions") {
      // 指定活動
      let num = 0;
      const whitelistedStorePromGroupIds =
        codeState.data.promotion?.conditions?.[0]?.whitelisted_store_prom_ids ||
        [];
      carts.forEach((cart) => {
        if (
          whitelistedStorePromGroupIds.includes(cart?.event_group?.event_id)
        ) {
          codeState.amount += cart.sub_total;
          num++;
        }
      });
      if (!num) {
        return Promise.reject("購物車中的商品不適用此折扣碼");
      }
    } else if (conditionScope == "promotion_groups") {
      // 指定活動組
      let num = 0;
      const whitelistedStorePromGroupIds =
        codeState.data.promotion?.conditions?.[0]
          ?.whitelisted_store_prom_group_ids || [];

      const whitelistedStorePromUuids = Object.values(props.promotionMap)
        .filter((p) =>
          !!p?.parent_id
            ? whitelistedStorePromGroupIds.includes(p.parent_id)
            : whitelistedStorePromGroupIds.includes(p.id)
        )
        .map((p) => p.event_group_uuid);
      // console.debug("promotionMap", Object.values(props.promotionMap))

      carts.forEach((cart) => {
        if (
          whitelistedStorePromUuids.includes(
            cart?.event_group?.event_group_uuid
          )
        ) {
          codeState.amount += cart.sub_total;
          num++;
        }
      });
      if (!num) {
        return Promise.reject("購物車中的商品不適用此折扣碼");
      }
    }
    // // 檢查最低消費
    // if (
    //   codeState.amount < codeState.data?.promotion?.conditions[0].min_amount
    // ) {
    //   const amount = Number(
    //     codeState.data?.promotion?.conditions[0].min_amount - codeState.amount
    //   );
    //   return Promise.reject("最低消費還差 $" + currencyFormat(amount));
    // }
    return Promise.resolve();
  }
};

const handleDiscountCode = async (formEl) => {
  try {
    if (!formEl) return;
    // formEl.clearValidate()
    form.value.prom_code_id = null;
    chkCode(codeState.code);

    if (!codeState.data) {
      const resp = await handleCreateDiscountCode();
      // console.log("handleCreateDiscountCode: ", resp)
      if (resp) {
        codeState.items = [];
        await onClickDiscountCode();
        codeState.data = codeState.items.find((el) => el.id == resp.id);
      }
    }
    formEl.validate((valid, fields) => {
      if (valid) {
        // form.value.prom_code_id = codeState.data.id;
        form.value.prom_code_id = codeState.data;
        // console.log("formRef: ", form.value)
        // console.log('amount', promotionCodeDiscount.value)
        emit("ok", { amount: promotionCodeDiscount.value, ...codeState.data });
        codeState.visible = false;
        codeState.data = null;
        codeState.code = null;
      } else {
        console.log("error submit!", fields);
        return false;
      }
    });
  } catch (err) {
    console.log("handleDiscountCode err: ", err);
    return false;
  }
};

const handleCreateDiscountCode = async () => {
  store.dispatch("lite/setFullPageLoading", true);
  try {
    const params = {
      // store_id: storeId.value,
      code: codeState.code,
      action: "get_or_create",
    };
    const resp = await _api_postAccountPromotionCodes(params);
    // console.log("handleDiscountCode: ", resp.data.store_prom_id)
    return resp.data;
  } catch (err) {
    notificationErrors(err.response.data);
    codeState.code = null;
  } finally {
    store.dispatch("lite/setFullPageLoading", false);
  }
};

const onClickPromo = (item) => {
  if (codeFormRef.value) {
    codeFormRef.value.clearValidate();
  }
  // console.debug("onClickPromo: ", item)
  if (item.is_valid) {
    codeState.data = item;
    codeState.code = item?.promotion?.codes[0];
    handleDiscountCode(codeFormRef.value);
  }
};

const removeDiscountCode = () => {
  swal
    .fire({
      title: t("不使用此折扣碼？"),
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dd3333",
      cancelButtonColor: "#78909C",
      confirmButtonText: t("確認不使用"),
      cancelButtonText: t("按錯了!"),
    })
    .then((result) => {
      if (result.value) {
        codeState.data = null;
        codeState.code = null;
        form.value.prom_code_id = null;
        emit("ok", codeState.data);
      }
    });
};

const allowUseDiscountCode = (isAllowUse) => {
  const emitData = {
    ...props.state.discountCode,
    isAllowUse,
  };
  emit("ok", emitData);
};

</script>

<style lang="scss">
.discount-code-sector {
  .btn-use-discount-code {
    font-size: 1rem;

    i {
      margin-left: 0 !important;
      margin-right: 5px !important;
    }
  }

  .el-dialog-use-discount-code {
    .el-dialog {
      overflow-x: hidden !important;
    }
  }
}
</style>
